import { createFileRoute } from "@tanstack/react-router";
import type { ClientLocationBaseData, ClientLocationHydrated } from "api-types/locations/locationsResponses";
import { assertIsNotUndefined } from "common/utils/assertOfType";
import CommunityLayout from "lib/components/builders/communities/CommunityLayout";
import SubLocations from "lib/components/builders/communities/plans/SubLocations";
import { calculateExpansionMap, createDescendantMap } from "lib/components/builders/communities/utils/locationUtils";
import { getBuilderLocationBaseDataURL, getBuilderLocationChildrenURL } from "lib/endpointHelpers";
import { useFetch } from "lib/fetcher";
import { useAppRouterParams } from "lib/routeHelpers";
import { useMemo, useState } from "react";
import Loading from "ui/components/Loading";
import withAuthentication from "../../../../../../components/withAuthentication";

function SublocationsPageReady() {
  const { builderId, locationId } = useAppRouterParams();

  if (!builderId || !locationId) {
    return null;
  }

  return <SubLocationsPage builderId={builderId} locationId={locationId} />;
}

function SubLocationsPage({ builderId, locationId }: { builderId: string; locationId: string }) {
  assertIsNotUndefined(builderId);
  assertIsNotUndefined(locationId);
  const [search, setSearch] = useState("");
  const [showArchived, setShowArchived] = useState(false);

  const { data, loading } = useFetch<ClientLocationBaseData>(getBuilderLocationBaseDataURL({ builderId, locationId }), [
    builderId,
    locationId,
  ]);

  // Fetch all children to build the descendant map for search
  const { data: allChildLocations } = useFetch<ClientLocationHydrated[]>(
    getBuilderLocationChildrenURL({ builderId, locationId, includeArchived: true }),
    [builderId, locationId],
  );

  const descendantsByParent = useMemo(() => createDescendantMap(allChildLocations || []), [allChildLocations]);

  const { mustExpand } = useMemo(() => {
    if (!search || !allChildLocations) {
      return { mustExpand: {} };
    }

    const expand = calculateExpansionMap(allChildLocations, search, descendantsByParent, showArchived);

    return { mustExpand: expand };
  }, [search, allChildLocations, descendantsByParent, showArchived]);

  return (
    <CommunityLayout currentPage="locations">
      {loading || !data ? (
        <Loading />
      ) : (
        <SubLocations
          parentLocation={data.location}
          builderId={builderId}
          locationName={data.location.name}
          locationId={locationId}
          emptyCTA
          search={search}
          setSearch={setSearch}
          forcedExpanded={mustExpand}
          showArchived={showArchived}
          setShowArchived={setShowArchived}
        />
      )}
    </CommunityLayout>
  );
}

export const Route = createFileRoute("/builders/$builder_id/locations/$location_id/sub-locations/")({
  component: withAuthentication(SublocationsPageReady, {
    permissions: ["VIEW_CHILD_LOCATIONS"],
    resource: {
      resourceType: "communities",
      routeParamName: "location_id",
    },
  }),
});
